import { useState, useEffect, useCallback } from "react";
import { Wifi, WifiOff, RefreshCw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { getQueueCount, processQueue } from "@/lib/offlineQueue";
import api from "@/lib/api";
import { toast } from "sonner";
import logger from "@/lib/logger";

function useOnlineStatus() {
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  useEffect(() => {
    const goOnline = () => setIsOnline(true);
    const goOffline = () => setIsOnline(false);
    window.addEventListener("online", goOnline);
    window.addEventListener("offline", goOffline);
    return () => {
      window.removeEventListener("online", goOnline);
      window.removeEventListener("offline", goOffline);
    };
  }, []);
  return isOnline;
}

function usePendingSync() {
  const [pendingCount, setPendingCount] = useState(0);
  const [syncing, setSyncing] = useState(false);

  const refreshPending = useCallback(async () => {
    try {
      const count = await getQueueCount();
      setPendingCount(count || 0);
    } catch (err) { logger.error("Failed to get pending queue:", err); }
  }, []);

  useEffect(() => {
    refreshPending();
    const interval = setInterval(refreshPending, 10000);
    return () => clearInterval(interval);
  }, [refreshPending]);

  const syncAll = useCallback(async () => {
    setSyncing(true);
    try {
      const result = await processQueue(api);
      if (result?.synced > 0) {
        toast.success(`${result.synced} entries synced`);
      }
      if (result?.failed > 0) {
        toast.error(`${result.failed} entries failed to sync`);
      }
      await refreshPending();
    } catch (err) {
      logger.error("Sync failed:", err);
      toast.error("Sync failed");
    }
    setSyncing(false);
  }, [refreshPending]);

  return { pendingCount, syncing, syncAll };
}

export default function OfflineBanner() {
  const isOnline = useOnlineStatus();
  const { pendingCount, syncing, syncAll } = usePendingSync();

  if (isOnline && pendingCount === 0) return null;

  return (
    <div className={`fixed bottom-0 left-0 right-0 z-50 transition-all ${isOnline ? "bg-amber-500" : "bg-red-600"}`}>
      <div className="max-w-6xl mx-auto px-4 py-2 flex items-center justify-between">
        <div className="flex items-center gap-2 text-white">
          {isOnline ? <Wifi size={16} /> : <WifiOff size={16} />}
          <span className="text-sm font-medium">
            {isOnline
              ? `${pendingCount} pending entries to sync`
              : "You are offline \u2014 entries saved locally"}
          </span>
        </div>
        {isOnline && pendingCount > 0 && (
          <Button
            size="sm"
            onClick={syncAll}
            disabled={syncing}
            className="h-7 text-xs bg-white text-amber-700 hover:bg-amber-50"
            data-testid="sync-now-btn"
          >
            <RefreshCw size={12} className={`mr-1 ${syncing ? "animate-spin" : ""}`} />
            {syncing ? "Syncing..." : "Sync Now"}
          </Button>
        )}
        {!isOnline && (
          <span className="text-white/80 text-xs">
            {pendingCount > 0 ? `${pendingCount} saved offline` : "All entries synced"}
          </span>
        )}
      </div>
    </div>
  );
}
